import React, { useMemo, useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Platform, Image, ActivityIndicator, Pressable, Alert } from 'react-native';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import Footer from '../components/Footer';
import Tooltip from '../components/Tooltip';
import {
  validateRequired,
  validateEmail,
  validatePAN,
  validateISODate,
  isBeforeOrEqualDate,
  validateGSTIN,
  validatePostalCode,
  validateHSN,
  validateNumber,
} from '../utils/formatters';
const currencySymbols = { INR: '₹', USD: '$', EUR: '€' };
const isWeb = Platform.OS === 'web';
// Simple SectionCard for consistent visuals
const SectionCard = React.memo(({ title, children, right }) => (
  <View style={[styles.card, { width: '100%' }]}> 
    <View style={styles.cardHeader}>
      <Text style={styles.cardTitle}>{title}</Text>
      {right}
    </View>
    {children}
  </View>
));
const QuotationMakerScreen = () => {
  const { session } = useAuth?.() || { session: null };
  const route = useRoute();
  // Track screen dimensions reactively like the invoice screen
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setScreenData(window));
    return () => sub?.remove?.();
  }, []);
  const isMobile = screenData.width < 900;
  const navigation = useNavigation();

  // Align preview scaling with Invoice Generator so it fits inside the SectionCard
  const leftPanelShare = isMobile ? 1 : 0.6;
  const rightPanelShare = isMobile ? 1 : 0.4;
  const previewScale = useMemo(() => {
    const outerPadding = 30; // calculatorContainer horizontal padding
    const gapBetweenColumns = 24; // styles.calculatorRow gap
    const cardPadding = 16; // styles.card horizontal padding
    const borderFudge = 12; // extra room for borders/scrollbar
    const innerBuffer = 20; // keep preview away from card edge
    const pageWidth = 794; // A4 width in px
    const shrinkFactor = 0.85; // render slightly smaller to fit comfortably in card

    const containerWidth = screenData.width - outerPadding * 2;
    const availableRowWidth = Math.max(0, containerWidth - gapBetweenColumns);
    const panelWidth = isMobile ? containerWidth : availableRowWidth * rightPanelShare;
    const availableInner = Math.max(0, panelWidth - cardPadding * 2 - borderFudge - innerBuffer);
    const scale = Math.max(0.3, Math.min((availableInner * shrinkFactor) / pageWidth, 1));
    return scale;
  }, [screenData.width, isMobile]);

  

  const [quote, setQuote] = useState({
    title: 'Quotation',
    number: 'Q00001',
    quoteDate: '',
    validUntil: '',
    currency: 'INR',
    dueDate: '',
    defaultGST: 0,
  });
  const [seller, setSeller] = useState({
    businessName: '',
    address: '', city: '', postalCode: '', state: '', country: '',
    gstin: '', pan: '', email: '', phone: '',
    customFields: [],
  });
  const [client, setClient] = useState({
    businessName: '',
    address: '', city: '', postalCode: '', state: '', country: '',
    gstin: '', pan: '', email: '', phone: '',
    customFields: [],
  });
  const [shipping, setShipping] = useState({ enabled: false, address: '', city: '', postalCode: '', state: '', notes: '' });
  const [columns, setColumns] = useState({ showHSN: true, showGST: true, showCGSTSGST: true, showImage: false });
  const [items, setItems] = useState([
    { id: 1, name: '', description: '', hsnSac: '', gstRate: 0, qty: 1, rate: 0, imageUri: '' },
  ]);
  const [charges, setCharges] = useState({ addlCharges: 0, discount: 0, roundingMode: 'none', hideTotals: false, summarizeQty: false, showTotalInWords: true });
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [signatureUri, setSignatureUri] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [metaCustomFields, setMetaCustomFields] = useState([]);
  const [logoUri, setLogoUri] = useState('');
  const [showDueDate, setShowDueDate] = useState(false);
  const [showCharges, setShowCharges] = useState(true);
  const [columnsEditorOpen, setColumnsEditorOpen] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const [showMetaCustomFields, setShowMetaCustomFields] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [template, setTemplate] = useState('classic'); // 'classic' or 'emerald'

  // Helper to format local date as YYYY-MM-DD
  const formatYYYYMMDD = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Prefill quote date with today's date on mount if empty
  useEffect(() => {
    if (!quote.quoteDate) {
      setQuote((p) => ({ ...p, quoteDate: formatYYYYMMDD(new Date()) }));
    }
  }, []);

  // Safely parse JSON-like fields that may be stored as strings
  const parseMaybeJson = (val, fallback) => {
    if (val == null) return fallback;
    if (typeof val === 'string') {
      try { return JSON.parse(val); } catch (_) { return fallback; }
    }
    return val;
  };

  // Apply a loaded quotation row into UI state
  const loadQuoteFromRow = (row) => {
    const nextSeller = parseMaybeJson(row.seller, seller);
    const nextClient = parseMaybeJson(row.client, client);
    const nextShipping = parseMaybeJson(row.shipping, shipping);
    const nextItems = parseMaybeJson(row.items, items);
    const nextCharges = parseMaybeJson(row.charges, charges);
    const nextColumns = parseMaybeJson(row.columns_config, columns);
    const nextAttachments = parseMaybeJson(row.attachments, []);

    setQuote((p) => ({
      ...p,
      title: row.title || p.title,
      number: row.quote_number || p.number,
      quoteDate: row.quote_date || p.quoteDate,
      validUntil: row.valid_until || p.validUntil,
      dueDate: row.due_date || p.dueDate,
      currency: row.currency || p.currency,
    }));
    setSeller(nextSeller || seller);
    setClient(nextClient || client);
    setShipping(nextShipping || shipping);
    setItems(Array.isArray(nextItems) ? nextItems : items);
    setCharges(nextCharges || charges);
    setColumns(nextColumns || columns);
    setNotes(typeof row.notes === 'string' ? row.notes : (row.notes ?? ''));
    setTerms(typeof row.terms === 'string' ? row.terms : (row.terms ?? ''));
    setContactDetails(typeof row.contact_details === 'string' ? row.contact_details : (row.contact_details ?? ''));
    setAdditionalInfo(typeof row.additional_info === 'string' ? row.additional_info : (row.additional_info ?? ''));
    setAttachments(Array.isArray(nextAttachments) ? nextAttachments : []);
    setLogoUri(typeof row.logo_uri === 'string' ? row.logo_uri : (row.logo_uri ?? ''));
    setSignatureUri(typeof row.signature_uri === 'string' ? row.signature_uri : (row.signature_uri ?? ''));
    setTemplate(typeof row.template_id === 'string' ? row.template_id : (row.template_id ?? 'classic'));

    // Toggle helpful UI sections based on loaded content
    const hasDue = !!row.due_date;
    if (hasDue) setShowDueDate(true);
    const hasCharges = nextCharges && (nextCharges.addlCharges || nextCharges.discount);
    if (hasCharges) setShowCharges(true);
    if (row.notes) setShowNotes(true);
    if (row.terms) setShowTerms(true);
    if (row.contact_details) setShowContact(true);
    if (row.signature_uri) setShowSignature(true);
    if (Array.isArray(nextAttachments) && nextAttachments.length) setShowAttachments(true);
  };

  // Load by id when navigated from Dashboard; prehydrate instantly and fetch concurrently
  useEffect(() => {
    const loadFromParams = async () => {
      const id = route?.params?.loadId;
      const snapshot = route?.params?.rowSnapshot;
      const shouldDownload = !!route?.params?.download;
      if (!id) return;

      // 1) Prehydrate from snapshot or local cache for instant UI
      try {
        if (snapshot && typeof snapshot === 'object') {
          loadQuoteFromRow(snapshot);
        } else if (Platform.OS === 'web') {
          const key = `quote_prefetch_${id}`;
          try {
            const raw = window.localStorage.getItem(key);
            if (raw) {
              const cached = JSON.parse(raw);
              if (cached && typeof cached === 'object') {
                loadQuoteFromRow(cached);
              }
            }
          } catch (_) {}
        }
      } catch (_) {}

      // 2) Fetch via SDK and PostgREST concurrently; apply whichever returns first
      const token = session?.access_token || session?.accessToken || null;
      const restUrl = `${SUPABASE_URL}/rest/v1/quotation_maker?id=eq.${id}&select=*`;
      const restHeaders = { apikey: SUPABASE_ANON_KEY, Accept: 'application/json' };
      if (token) restHeaders['Authorization'] = `Bearer ${token}`;

      const sdkPromise = supabase
        .from('quotation_maker')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      const restPromise = (async () => {
        try {
          const resp = await fetch(restUrl, { headers: restHeaders });
          const text = await resp.text();
          let json = null;
          try { json = JSON.parse(text); } catch (_) {}
          const data = Array.isArray(json) ? json[0] || null : null;
          return { data, error: resp.ok ? null : { message: `HTTP ${resp.status}` } };
        } catch (e) {
          return { data: null, error: { message: e?.message || 'REST error' } };
        }
      })();

      const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out', code: 'ETIMEOUT' } }), 6000));

      let winner = null;
      try {
        winner = await Promise.race([sdkPromise, restPromise, timeoutPromise]);
      } catch (e) {
        winner = null;
      }
      if (winner && winner.data) {
        loadQuoteFromRow(winner.data);
      }

      // 3) Auto-download if requested (mirrors Invoice screen behavior)
      if (shouldDownload) {
        await new Promise((r) => setTimeout(r, 150));
        await exportPDF();
      }
    };
    loadFromParams();
  }, [route?.params?.loadId, route?.params?.rowSnapshot, route?.params?.download]);

  const updateItem = (id, field, value) => {
    setItems(prev => prev.map(it => {
      if (it.id !== id) return it;
      const patch = { [field]: value };
      if (field === 'qty' || field === 'rate' || field === 'gstRate') {
        patch[field] = Number(value) || 0;
      }
      return { ...it, ...patch };
    }));
  };
  const addItem = () => {
    const nextId = (items[items.length - 1]?.id || 0) + 1;
    setItems(prev => [...prev, { id: nextId, name: '', description: '', hsnSac: '', gstRate: quote.defaultGST || 0, qty: 1, rate: 0, imageUri: '' }]);
  };
  const removeItem = (id) => setItems(prev => prev.filter(it => it.id !== id));
  const duplicateItem = (id) => {
    setItems(prev => {
      const idx = prev.findIndex(it => it.id === id);
      if (idx === -1) return prev;
      const base = prev[idx];
      const nextId = (prev[prev.length - 1]?.id || 0) + 1;
      const copy = { ...base, id: nextId };
      const out = [...prev];
      out.splice(idx + 1, 0, copy);
      return out;
    });
  };

  // Validation: errors state and helpers
  const [errors, setErrors] = useState({ quote: {}, seller: {}, client: {}, shipping: {}, items: {} });

  const validateParty = (party) => {
    const e = {};
    if (!validateRequired(party.businessName)) e.businessName = 'Business name is required';
    if (party.email && !validateEmail(party.email)) e.email = 'Invalid email';
    if (party.pan && !validatePAN(party.pan)) e.pan = 'Invalid PAN';
    if (party.gstin && !validateGSTIN(party.gstin)) e.gstin = 'Invalid GSTIN';
    if (party.postalCode && !validatePostalCode(party.country, party.postalCode)) e.postalCode = 'Invalid postal code';
    return e;
  };

  const validateQuoteMeta = (q) => {
    const e = {};
    if (q.quoteDate && !validateISODate(q.quoteDate)) e.quoteDate = 'Use YYYY-MM-DD';
    if (q.validUntil) {
      if (!validateISODate(q.validUntil)) e.validUntil = 'Use YYYY-MM-DD';
      else if (!isBeforeOrEqualDate(q.quoteDate, q.validUntil)) e.validUntil = 'Valid until must be after quote date';
    }
    if (showDueDate && q.dueDate) {
      if (!validateISODate(q.dueDate)) e.dueDate = 'Use YYYY-MM-DD';
      else if (!isBeforeOrEqualDate(q.quoteDate, q.dueDate)) e.dueDate = 'Due date must be after quote date';
    }
    return e;
  };

  const validateShippingMeta = (s) => {
    const e = {};
    const countryHint = seller?.country || client?.country || 'India';
    if (s.postalCode && !validatePostalCode(countryHint, s.postalCode)) e.postalCode = 'Invalid postal code';
    return e;
  };

  const validateLine = (line) => {
    const e = {};
    if (!validateRequired(line.name)) e.name = 'Item name is required';
    if (line.hsnSac && !validateHSN(line.hsnSac)) e.hsnSac = 'Invalid HSN/SAC';
    const qtyNum = Number(line.qty);
    const rateNum = Number(line.rate);
    const gstNum = Number(line.gstRate);
    if (!validateNumber(qtyNum)) e.qty = 'Qty must be a number';
    if (!validateNumber(rateNum)) e.rate = 'Rate must be a number';
    if (!validateNumber(gstNum) || gstNum < 0 || gstNum > 28) e.gstRate = 'GST must be 0–28';
    return e;
  };

  useEffect(() => {
    const next = {
      quote: validateQuoteMeta(quote),
      seller: validateParty(seller),
      client: validateParty(client),
      shipping: validateShippingMeta(shipping),
      items: {},
    };
    items.forEach((it) => { next.items[it.id] = validateLine(it); });
    setErrors(next);
  }, [quote, seller, client, shipping, items, showDueDate]);

  // Web-only image upload for attachments
  const attachmentsInputRef = useRef(null);
  const handleAttachmentPick = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setAttachments((prev) => [...prev, dataUrl]);
      };
      reader.readAsDataURL(file);
    });
    try { e.target.value = ''; } catch (_) {}
  };

  // Web-only image upload for logo
  const logoInputRef = useRef(null);
  const handleLogoPick = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoUri(String(dataUrl || ''));
    };
    reader.readAsDataURL(file);
    try { e.target.value = ''; } catch (_) {}
  };

  // Web-only image upload for signature
  const signatureInputRef = useRef(null);
  const handleSignaturePick = (e) => {
    const files = Array.from(e?.target?.files || []);
    if (!files.length) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setSignatureUri(String(dataUrl || ''));
    };
    reader.readAsDataURL(file);
    try { e.target.value = ''; } catch (_) {}
  };

  // PDF export (web-only) for Quotation
  const exportPDF = async () => {
    try {
      if (!isWeb) return; // web-only export
      const modCanvas = await import('html2canvas');
      const modPdf = await import('jspdf');
      const html2canvas = modCanvas.default;
      const { jsPDF } = modPdf;

      const target = document.getElementById('quotation-print');
      if (!target) return;
      const canvas = await html2canvas(target, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 40; // margin
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let y = 20;
      if (imgHeight <= pageHeight - 40) {
        pdf.addImage(imgData, 'PNG', 20, y, imgWidth, imgHeight);
      } else {
        let heightLeft = imgHeight;
        let position = 20;
        while (heightLeft > 0) {
          pdf.addImage(imgData, 'PNG', 20, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          if (heightLeft > 0) {
            pdf.addPage();
            position = 20 - (imgHeight - heightLeft);
          }
        }
      }
      pdf.save(`Quotation_${quote.number}.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  // Number to words helper (Indian numbering system) used in preview and save payload
  const numberToWords = (num) => {
    const a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
    const inWords = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n/10)] + (n%10? ' ' + a[n%10] : '');
      if (n < 1000) return a[Math.floor(n/100)] + ' hundred' + (n%100? ' ' + inWords(n%100):'');
      if (n < 100000) return inWords(Math.floor(n/1000)) + ' thousand' + (n%1000? ' ' + inWords(n%1000):'');
      if (n < 10000000) return inWords(Math.floor(n/100000)) + ' lakh' + (n%100000? ' ' + inWords(n%100000):'');
      return inWords(Math.floor(n/10000000)) + ' crore' + (n%10000000? ' ' + inWords(n%10000000):'');
    };
    const rounded = Math.round(Number(num) || 0);
    if (!rounded) return 'zero';
    return inWords(rounded) + ' only';
  };

  const summary = useMemo(() => {
    const subtotal = items.reduce((acc, it) => acc + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
    const taxTotal = items.reduce((acc, it) => acc + ((Number(it.qty) || 0) * (Number(it.rate) || 0)) * ((Number(it.gstRate) || 0) / 100), 0);
    const cgstTotal = taxTotal / 2;
    const sgstTotal = taxTotal / 2;
    const discount = Number(charges.discount) || 0;
    const addl = Number(charges.addlCharges) || 0;
    let total = Math.max(0, subtotal - discount + addl + taxTotal);
    // Round to nearest rupee if requested
    if (charges.roundingMode === 'up') {
      total = Math.ceil(total);
    } else if (charges.roundingMode === 'down') {
      total = Math.floor(total);
    }
    return { subtotal, taxTotal, cgstTotal, sgstTotal, discount, addl, total };
  }, [items, charges]);

  const currencySymbol = currencySymbols[quote.currency] || '';

  const renderTopMeta = () => (
    <SectionCard title="Quotation Details" right={(
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <TouchableOpacity style={styles.outlineButton} onPress={()=>setShowDueDate(p=>!p)}><Text style={styles.outlineButtonText}>{showDueDate? 'Hide due date' : 'Add due date'}</Text></TouchableOpacity>
        <TouchableOpacity style={styles.outlineButton} onPress={()=>setShowMetaCustomFields(p=>!p)}><Text style={styles.outlineButtonText}>{showMetaCustomFields? 'Hide Custom Fields' : 'Add Custom Fields'}</Text></TouchableOpacity>
      </View>
    )}>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>        
        <View style={[styles.half, { gap: 12 }]}>          
          <Text style={styles.label}>Quote No</Text>
          <TextInput
            style={styles.input}
            value={quote.number}
            onChangeText={(v)=>setQuote(p=>({ ...p, number: v.toUpperCase() }))}
            placeholder="Q00001"
            autoCapitalize="characters"
            autoCorrect={false}
            spellCheck={false}
            autoComplete="off"
            selectTextOnFocus
            maxLength={24}
          />
        </View>
        <View style={[styles.half, { gap: 12 }]}>          
          <Text style={styles.label}>Quote Date</Text>
          {isWeb ? (
            React.createElement('input', {
              type: 'date',
              value: quote.quoteDate || '',
              onChange: (e) => setQuote((p) => ({ ...p, quoteDate: e.target.value })),
              style: {
                padding: '9px 12px',
                height: 40,
                borderRadius: 8,
                border: `1px solid ${errors.quote?.quoteDate ? '#ef4444' : '#e5e7eb'}`,
                backgroundColor: errors.quote?.quoteDate ? '#fff1f2' : '#f8fafc',
                fontSize: 14,
                color: '#0f172a',
                outline: 'none',
              },
            })
          ) : (
            <TextInput style={[styles.input, errors.quote?.quoteDate && styles.inputError]} value={quote.quoteDate} onChangeText={(v)=>setQuote(p=>({ ...p, quoteDate: v }))} placeholder="YYYY-MM-DD" autoCorrect={false} spellCheck={false} autoComplete="off" />
          )}
          {!!errors.quote?.quoteDate && (<Text style={styles.errorText}>{errors.quote.quoteDate}</Text>)}
          <View style={{ marginTop: 8 }}>
            <Text style={styles.label}>Valid Until</Text>
            {isWeb ? (
              React.createElement('input', {
                type: 'date',
                value: quote.validUntil || '',
                onChange: (e) => setQuote((p) => ({ ...p, validUntil: e.target.value })),
                style: {
                  padding: '9px 12px',
                  height: 40,
                  borderRadius: 8,
                  border: `1px solid ${errors.quote?.validUntil ? '#ef4444' : '#e5e7eb'}`,
                  backgroundColor: errors.quote?.validUntil ? '#fff1f2' : '#f8fafc',
                  fontSize: 14,
                  color: '#0f172a',
                  outline: 'none',
                },
              })
            ) : (
              <TextInput style={[styles.input, errors.quote?.validUntil && styles.inputError]} value={quote.validUntil} onChangeText={(v)=>setQuote(p=>({ ...p, validUntil: v }))} placeholder="YYYY-MM-DD" autoCorrect={false} spellCheck={false} autoComplete="off" />
            )}
            {!!errors.quote?.validUntil && (<Text style={styles.errorText}>{errors.quote.validUntil}</Text>)}
          </View>
          {showDueDate && (
            <View style={{ marginTop: 8 }}>
              <Text style={styles.label}>Due Date</Text>
              {isWeb ? (
                React.createElement('input', {
                  type: 'date',
                  value: quote.dueDate || '',
                  onChange: (e) => setQuote((p) => ({ ...p, dueDate: e.target.value })),
                  style: {
                    padding: '9px 12px',
                    height: 40,
                    borderRadius: 8,
                    border: `1px solid ${errors.quote?.dueDate ? '#ef4444' : '#e5e7eb'}`,
                    backgroundColor: errors.quote?.dueDate ? '#fff1f2' : '#f8fafc',
                    fontSize: 14,
                    color: '#0f172a',
                    outline: 'none',
                  },
                })
              ) : (
                <TextInput style={[styles.input, errors.quote?.dueDate && styles.inputError]} value={quote.dueDate} onChangeText={(v)=>setQuote(p=>({ ...p, dueDate: v }))} placeholder="YYYY-MM-DD" autoCorrect={false} spellCheck={false} autoComplete="off" />
              )}
              {!!errors.quote?.dueDate && (<Text style={styles.errorText}>{errors.quote.dueDate}</Text>)}
            </View>
          )}
          {/* Currency selection moved to Line Items header */}
          {/* Removed Default GST % (new items) field from UI */}
          <View style={{ marginTop: 8 }}>
            <Text style={styles.label}>Logo</Text>
            {isWeb ? (
              <View>
                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                  <TouchableOpacity style={styles.outlineButton} onPress={()=>{ try { logoInputRef.current?.click(); } catch(_){} }}>
                    <Text style={styles.outlineButtonText}>Upload Logo</Text>
                  </TouchableOpacity>
                  <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoPick} />
                </View>
                {!!logoUri && (
                  <View style={{ marginTop: 8 }}>
                    <Image source={{ uri: logoUri }} style={{ width: 80, height: 80, borderRadius: 8 }} />
                  </View>
                )}
              </View>
            ) : (
              <TextInput style={styles.input} value={logoUri} onChangeText={setLogoUri} placeholder="https://.../logo.png" autoCorrect={false} spellCheck={false} autoComplete="off" />
            )}
          </View>
        </View>
      </View>
      {showMetaCustomFields && (
        <View style={{ marginTop: 8 }}>
          <Text style={styles.label}>Custom Fields</Text>
          <View style={{ gap: 8 }}>
            {metaCustomFields.map((f, idx) => (
              <View key={idx} style={styles.inputRow}>
                <TextInput style={[styles.input, { flex: 1 }]} value={f.label} onChangeText={(v)=>{
                  setMetaCustomFields(prev=>prev.map((pf,i)=>i===idx?{...pf,label:v}:pf));
                }} placeholder="Label" />
                <TextInput style={[styles.input, { flex: 2 }]} value={f.value} onChangeText={(v)=>{
                  setMetaCustomFields(prev=>prev.map((pf,i)=>i===idx?{...pf,value:v}:pf));
                }} placeholder="Value" />
                <TouchableOpacity style={styles.iconButton} onPress={()=>{
                  setMetaCustomFields(prev=>prev.filter((_,i)=>i!==idx));
                }}><Text>✕</Text></TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={styles.outlineButton} onPress={()=>setMetaCustomFields(prev=>[...prev,{label:'',value:''}])}><Text style={styles.outlineButtonText}>Add Field</Text></TouchableOpacity>
          </View>
        </View>
      )}
    </SectionCard>
  );

  const renderPartyForm = (party, setParty, keyName) => (
    <View style={{ gap: 12 }}>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={styles.half}>
          <Text style={styles.label}>Country</Text>
          <TextInput style={styles.input} value={party.country} onChangeText={(v)=>setParty(p=>({ ...p, country: v }))} placeholder="India" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Business Name</Text>
          <TextInput style={[styles.input, errors[keyName]?.businessName && styles.inputError]} value={party.businessName} onChangeText={(v)=>setParty(p=>({ ...p, businessName: v }))} placeholder="Your Business Name" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.businessName && (<Text style={styles.errorText}>{errors[keyName].businessName}</Text>)}
        </View>
      </View>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={styles.half}>
          <Text style={styles.label}>Address</Text>
          <TextInput style={styles.input} value={party.address} onChangeText={(v)=>setParty(p=>({ ...p, address: v }))} placeholder="Street address" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>City</Text>
          <TextInput style={styles.input} value={party.city} onChangeText={(v)=>setParty(p=>({ ...p, city: v }))} placeholder="City" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
      </View>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={styles.half}>
          <Text style={styles.label}>Postal Code</Text>
          <TextInput style={[styles.input, errors[keyName]?.postalCode && styles.inputError]} value={party.postalCode} onChangeText={(v)=>setParty(p=>({ ...p, postalCode: v }))} placeholder="123456" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.postalCode && (<Text style={styles.errorText}>{errors[keyName].postalCode}</Text>)}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>State</Text>
          <TextInput style={styles.input} value={party.state} onChangeText={(v)=>setParty(p=>({ ...p, state: v }))} placeholder="State" autoCorrect={false} spellCheck={false} autoComplete="off" />
        </View>
      </View>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={styles.half}>
          <Text style={styles.label}>Email</Text>
          <TextInput style={[styles.input, errors[keyName]?.email && styles.inputError]} value={party.email} onChangeText={(v)=>setParty(p=>({ ...p, email: v }))} placeholder="email@example.com" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.email && (<Text style={styles.errorText}>{errors[keyName].email}</Text>)}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>Phone</Text>
          <TextInput
            style={styles.input}
            value={party.phone}
            onChangeText={(v)=>setParty(p=>({ ...p, phone: v }))}
            placeholder="+9199999999"
            keyboardType={Platform.OS === 'web' ? 'numeric' : 'phone-pad'}
          />
        </View>
      </View>
      <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
        <View style={styles.half}>
          <Text style={styles.label}>GSTIN</Text>
          <TextInput style={[styles.input, errors[keyName]?.gstin && styles.inputError]} value={party.gstin} onChangeText={(v)=>setParty(p=>({ ...p, gstin: v }))} placeholder="22AAAAA0000A1Z5" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.gstin && (<Text style={styles.errorText}>{errors[keyName].gstin}</Text>)}
        </View>
        <View style={styles.half}>
          <Text style={styles.label}>PAN</Text>
          <TextInput style={[styles.input, errors[keyName]?.pan && styles.inputError]} value={party.pan} onChangeText={(v)=>setParty(p=>({ ...p, pan: v }))} placeholder="ABCDE1234F" autoCorrect={false} spellCheck={false} autoComplete="off" />
          {!!errors[keyName]?.pan && (<Text style={styles.errorText}>{errors[keyName].pan}</Text>)}
        </View>
      </View>
      {false && (
      <View style={{ marginTop: 8 }}>
        <Text style={styles.label}>Custom Fields</Text>
        <View style={{ gap: 8 }}>
          {(party.customFields || []).map((f, idx) => (
            <View key={idx} style={styles.inputRow}>
              <TextInput style={[styles.input, { flex: 1 }]} value={f.label} onChangeText={(v)=>{
                setParty(p=>({ ...p, customFields: p.customFields.map((pf,i)=>i===idx?{...pf,label:v}:pf) }))
              }} placeholder="Label" />
              <TextInput style={[styles.input, { flex: 2 }]} value={f.value} onChangeText={(v)=>{
                setParty(p=>({ ...p, customFields: p.customFields.map((pf,i)=>i===idx?{...pf,value:v}:pf) }))
              }} placeholder="Value" />
              <TouchableOpacity style={styles.iconButton} onPress={()=>{
                setParty(p=>({ ...p, customFields: p.customFields.filter((_,i)=>i!==idx) }))
              }}><Text>✕</Text></TouchableOpacity>
            </View>
          ))}
          <TouchableOpacity style={styles.outlineButton} onPress={()=>setParty(p=>({ ...p, customFields: [...(p.customFields||[]), {label:'',value:''}] }))}><Text style={styles.outlineButtonText}>Add Field</Text></TouchableOpacity>
        </View>
      </View>
      )}
    </View>
  );

  const renderParties = () => (
    <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>      
      <View style={[styles.formContent, isMobile ? { width: '100%' } : { flex: 0.5 }]}>        
        <SectionCard title="Quoted By (Your Details)">
          {renderPartyForm(seller, setSeller, 'seller')}
        </SectionCard>
      </View>
      <View style={[styles.summaryPanel, isMobile ? { width: '100%', marginTop: 16 } : { flex: 0.5 }]}>        
        <SectionCard title="Quoted To (Client Details)">
          {renderPartyForm(client, setClient, 'client')}
        </SectionCard>
      </View>
    </View>
  );

  const formatCurrency = (n) => `${currencySymbol}${(Number(n) || 0).toFixed(2)}`;

  const renderItems = () => (
    <SectionCard title="Line Items" right={(
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <TouchableOpacity style={styles.outlineButton} onPress={addItem}><Text style={styles.outlineButtonText}>Add</Text></TouchableOpacity>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <Text style={styles.helperText}>Currency</Text>
          <TouchableOpacity style={[styles.outlineButton, quote.currency==='INR' && styles.outlineButtonActive]} onPress={()=>setQuote(p=>({ ...p, currency: 'INR' }))}><Text style={styles.outlineButtonText}>INR</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, quote.currency==='USD' && styles.outlineButtonActive]} onPress={()=>setQuote(p=>({ ...p, currency: 'USD' }))}><Text style={styles.outlineButtonText}>USD</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, quote.currency==='EUR' && styles.outlineButtonActive]} onPress={()=>setQuote(p=>({ ...p, currency: 'EUR' }))}><Text style={styles.outlineButtonText}>EUR</Text></TouchableOpacity>
        </View>
        <TouchableOpacity style={styles.outlineButton} onPress={()=>setColumnsEditorOpen(p=>!p)}><Text style={styles.outlineButtonText}>Edit Columns/Formulas</Text></TouchableOpacity>
      </View>
    )}>
      {columnsEditorOpen && (
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
          <TouchableOpacity style={[styles.outlineButton, columns.showImage && styles.outlineButtonActive]} onPress={()=>setColumns(p=>({ ...p, showImage: !p.showImage }))}><Text style={styles.outlineButtonText}>{columns.showImage? 'Hide Image' : 'Add Image'}</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, columns.showHSN && styles.outlineButtonActive]} onPress={()=>setColumns(p=>({ ...p, showHSN: !p.showHSN }))}><Text style={styles.outlineButtonText}>{columns.showHSN? 'Hide HSN/SAC' : 'Add HSN/SAC'}</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, columns.showGST && styles.outlineButtonActive]} onPress={()=>setColumns(p=>({ ...p, showGST: !p.showGST }))}><Text style={styles.outlineButtonText}>{columns.showGST? 'Hide GST' : 'Edit GST'}</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, columns.showCGSTSGST && styles.outlineButtonActive]} onPress={()=>setColumns(p=>({ ...p, showCGSTSGST: !p.showCGSTSGST }))}><Text style={styles.outlineButtonText}>{columns.showCGSTSGST? 'Hide CGST/SGST' : 'Show CGST/SGST'}</Text></TouchableOpacity>
        </View>
      )}
      <View style={styles.itemHeaderRow}>
        <Text style={[styles.itemHeaderCell, { flex: 2 }]}>Item</Text>
        {columns.showHSN && <Text style={[styles.itemHeaderCell, { flex: 1 }]}>HSN/SAC</Text>}
        {columns.showGST && <Text style={[styles.itemHeaderCell, { flex: 1 }]}>GST %</Text>}
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Qty</Text>
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Rate</Text>
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Amount</Text>
        {columns.showCGSTSGST && <Text style={[styles.itemHeaderCell, { flex: 1 }]}>CGST</Text>}
        {columns.showCGSTSGST && <Text style={[styles.itemHeaderCell, { flex: 1 }]}>SGST</Text>}
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Total</Text>
        <Text style={[styles.itemHeaderCell, { flex: 1 }]}>Actions</Text>
      </View>
      {items.map((line) => {
        const baseAmount = (Number(line.qty) || 0) * (Number(line.rate) || 0);
        const taxAmt = baseAmount * ((Number(line.gstRate) || 0) / 100);
        const cgst = taxAmt / 2;
        const sgst = taxAmt / 2;
        const lineTotal = baseAmount + taxAmt;
        return (
          <View key={line.id} style={styles.itemRow}>
            <View style={[styles.itemCell, { flex: 2 }]}>          
              <TextInput style={[styles.input, errors.items?.[line.id]?.name && styles.inputError]} value={line.name} onChangeText={(v)=>updateItem(line.id,'name',v)} placeholder="Item name" autoCorrect={false} spellCheck={false} autoComplete="off" />
              {!!errors.items?.[line.id]?.name && (<Text style={styles.errorText}>{errors.items[line.id].name}</Text>)}
              <TextInput style={[styles.input, { marginTop: 6 }]} value={line.description} onChangeText={(v)=>updateItem(line.id,'description',v)} placeholder="Optional description" autoCorrect={false} spellCheck={false} autoComplete="off" />
              {columns.showImage && (
                <TextInput style={[styles.input, { marginTop: 6 }]} value={line.imageUri} onChangeText={(v)=>updateItem(line.id,'imageUri',v)} placeholder="Image URL (optional)" autoCorrect={false} spellCheck={false} autoComplete="off" />
              )}
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
                <TouchableOpacity style={styles.outlineButton} onPress={()=>updateItem(line.id,'description',line.description||'')}><Text style={styles.outlineButtonText}>Add Description</Text></TouchableOpacity>
                <TouchableOpacity style={styles.outlineButton} onPress={()=>setColumns(p=>({ ...p, showImage: true }))}><Text style={styles.outlineButtonText}>Add Image</Text></TouchableOpacity>
              </View>
            </View>
            {columns.showHSN && (
              <View style={[styles.itemCell, { flex: 1 }]}>          
                <TextInput style={[styles.input, errors.items?.[line.id]?.hsnSac && styles.inputError]} value={line.hsnSac} onChangeText={(v)=>updateItem(line.id,'hsnSac',v)} placeholder="HSN/SAC" autoCorrect={false} spellCheck={false} autoComplete="off" />
                {!!errors.items?.[line.id]?.hsnSac && (<Text style={styles.errorText}>{errors.items[line.id].hsnSac}</Text>)}
              </View>
            )}
            {columns.showGST && (
              <View style={[styles.itemCell, { flex: 1 }]}>          
                <TextInput style={[styles.input, errors.items?.[line.id]?.gstRate && styles.inputError]} value={String(line.gstRate)} onChangeText={(v)=>updateItem(line.id,'gstRate',v)} keyboardType="numeric" placeholder={String(quote.defaultGST||0)} autoCorrect={false} spellCheck={false} autoComplete="off" />
                {!!errors.items?.[line.id]?.gstRate && (<Text style={styles.errorText}>{errors.items[line.id].gstRate}</Text>)}
              </View>
            )}
            <View style={[styles.itemCell, { flex: 1 }]}>          
              <TextInput style={[styles.input, errors.items?.[line.id]?.qty && styles.inputError]} value={String(line.qty)} onChangeText={(v)=>updateItem(line.id,'qty',v)} keyboardType="numeric" placeholder="1" autoCorrect={false} spellCheck={false} autoComplete="off" />
              {!!errors.items?.[line.id]?.qty && (<Text style={styles.errorText}>{errors.items[line.id].qty}</Text>)}
            </View>
            <View style={[styles.itemCell, { flex: 1 }]}>          
              <TextInput style={[styles.input, errors.items?.[line.id]?.rate && styles.inputError]} value={String(line.rate)} onChangeText={(v)=>updateItem(line.id,'rate',v)} keyboardType="numeric" placeholder="1" autoCorrect={false} spellCheck={false} autoComplete="off" />
              {!!errors.items?.[line.id]?.rate && (<Text style={styles.errorText}>{errors.items[line.id].rate}</Text>)}
            </View>
            <View style={[styles.itemCell, { flex: 1 }]}>          
              <Text style={styles.itemValue}>{formatCurrency(baseAmount)}</Text>
            </View>
            {columns.showCGSTSGST && (
              <View style={[styles.itemCell, { flex: 1 }]}>          
                <Text style={styles.itemValue}>{formatCurrency(cgst)}</Text>
              </View>
            )}
            {columns.showCGSTSGST && (
              <View style={[styles.itemCell, { flex: 1 }]}>          
                <Text style={styles.itemValue}>{formatCurrency(sgst)}</Text>
              </View>
            )}
            <View style={[styles.itemCell, { flex: 1 }]}>          
              <Text style={styles.itemValue}>{formatCurrency(lineTotal)}</Text>
            </View>
            <View style={[styles.itemCell, { flex: 1, flexDirection: 'row', gap: 6 }]}>          
              <Tooltip content="Duplicate">
                <Pressable
                  style={({ pressed }) => [styles.iconButton, pressed && { transform: [{ scale: 0.96 }], opacity: 0.9 }]}
                  onPress={()=>duplicateItem(line.id)}
                >
                  <Ionicons name="copy-outline" size={18} color="#0f172a" />
                </Pressable>
              </Tooltip>
              <Tooltip content="Delete">
                <Pressable
                  style={({ pressed }) => [styles.iconButton, pressed && { transform: [{ scale: 0.96 }], opacity: 0.9, backgroundColor: '#fee2e2', borderColor: '#fecaca' }]}
                  onPress={()=>removeItem(line.id)}
                >
                  <Ionicons name="close" size={18} color="#b91c1c" />
                </Pressable>
              </Tooltip>
            </View>
          </View>
        );
      })}
    </SectionCard>
  );

  const renderTotals = () => (
    <SectionCard title="Totals">
      <View style={{ gap: 12 }}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Subtotal</Text>
          <Text style={styles.summaryValue}>{formatCurrency(summary.subtotal)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>CGST</Text>
          <Text style={styles.summaryValue}>{formatCurrency(summary.cgstTotal)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>SGST</Text>
          <Text style={styles.summaryValue}>{formatCurrency(summary.sgstTotal)}</Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>GST Total</Text>
          <Text style={styles.summaryValue}>{formatCurrency(summary.taxTotal)}</Text>
        </View>
        {showCharges && (
          <>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Additional Charges</Text>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <TextInput style={[styles.input, { width: 120 }]} value={String(charges.addlCharges)} onChangeText={(v)=>setCharges(p=>({ ...p, addlCharges: Number(v) || 0 }))} placeholder="0" keyboardType="numeric" />
                <Text style={styles.summaryValue}>{formatCurrency(summary.addl)}</Text>
              </View>
            </View>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Discount</Text>
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                <TextInput style={[styles.input, { width: 120 }]} value={String(charges.discount)} onChangeText={(v)=>setCharges(p=>({ ...p, discount: Number(v) || 0 }))} placeholder="0" keyboardType="numeric" />
                <Text style={styles.summaryValue}>-{formatCurrency(summary.discount)}</Text>
              </View>
            </View>
          </>
        )}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>         
          <TouchableOpacity style={[styles.outlineButton, charges.roundingMode==='none' && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, roundingMode: 'none' }))}><Text style={styles.outlineButtonText}>No Rounding</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.roundingMode==='up' && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, roundingMode: 'up' }))}><Text style={styles.outlineButtonText}>Round Up</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.roundingMode==='down' && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, roundingMode: 'down' }))}><Text style={styles.outlineButtonText}>Round Down</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.hideTotals && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, hideTotals: !p.hideTotals }))}><Text style={styles.outlineButtonText}>{charges.hideTotals?'Show Totals':'Hide Totals'}</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.summarizeQty && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, summarizeQty: !p.summarizeQty }))}><Text style={styles.outlineButtonText}>{charges.summarizeQty?'Hide Qty Sum':'Summarise Qty'}</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, charges.showTotalInWords && styles.outlineButtonActive]} onPress={()=>setCharges(p=>({ ...p, showTotalInWords: !p.showTotalInWords }))}><Text style={styles.outlineButtonText}>{charges.showTotalInWords?'Hide Total In Words':'Show Total In Words'}</Text></TouchableOpacity>
          <TouchableOpacity style={styles.outlineButton} onPress={()=>setShowCharges(p=>!p)}><Text style={styles.outlineButtonText}>{showCharges? 'Hide Discounts/Additional Charges' : 'Add Discounts/Additional Charges'}</Text></TouchableOpacity>
        </ScrollView>
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Grand Total ({quote.currency})</Text>
          <Text style={styles.totalValue}>{formatCurrency(summary.total)}</Text>
        </View>
        {charges.showTotalInWords && (
          <View>
            <Text style={styles.helperText}>Total in words: {numberToWords(summary.total)} {quote.currency}</Text>
          </View>
        )}
      </View>
    </SectionCard>
  );

  // Print-ready preview (A4), with four templates: classic, emerald (purple), minimal, and slate
  const renderPrintQuotation = () => {
    const isClassic = template === 'classic';
    const isEmerald = template === 'emerald';
    const isMinimal = template === 'minimal';
    const isSlate = template === 'slate';

    // Theme palette per template
    const themes = {
      classic: {
        accent: '#2563EB',
        accentSoft: '#DBEAFE',
        headerTitleColor: '#111827',
        brandTextColor: '#0f172a',
        blockBorder: '#e5e7eb',
        blockBg: '#ffffff',
        tableHeaderBg: '#DBEAFE',
        tableHeaderText: '#374151',
        grandColor: '#374151',
      },
      emerald: {
        accent: '#6D28D9',
        accentSoft: '#EDE9FE',
        headerTitleColor: '#6D28D9',
        brandTextColor: '#0f172a',
        blockBorder: '#6D28D9',
        blockBg: '#F5F3FF',
        tableHeaderBg: '#6D28D9',
        tableHeaderText: '#ffffff',
        grandColor: '#6D28D9',
      },
      minimal: {
        accent: '#111827',
        accentSoft: '#ffffff',
        headerTitleColor: '#111827',
        brandTextColor: '#111827',
        blockBorder: '#e5e7eb',
        blockBg: '#ffffff',
        tableHeaderBg: '#ffffff',
        tableHeaderText: '#111827',
        grandColor: '#111827',
      },
      slate: {
        accent: '#0F172A',
        accentSoft: '#1F2937',
        headerTitleColor: '#0F172A',
        brandTextColor: '#0f172a',
        blockBorder: '#e5e7eb',
        blockBg: '#ffffff',
        tableHeaderBg: '#1F2937',
        tableHeaderText: '#ffffff',
        grandColor: '#0F172A',
      },
    };
    const theme = isEmerald ? themes.emerald : isMinimal ? themes.minimal : isSlate ? themes.slate : themes.classic;

    const printStyles = {
      page: { width: 794, minHeight: 1123, backgroundColor: '#ffffff', padding: 24, borderWidth: 1, borderColor: '#e5e7eb', position: 'relative' },
      headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
      brand: { flexDirection: 'row', alignItems: 'center', gap: 8 },
      brandText: { fontSize: 18, fontWeight: '700', color: theme.brandTextColor },
      invMetaRight: { alignItems: 'flex-end' },
      invTitle: { fontSize: isMinimal ? 24 : 22, fontWeight: '800', color: theme.headerTitleColor },
      invNo: { fontSize: 14, color: '#374151', marginTop: 4 },
      block: { borderWidth: 1, borderColor: theme.blockBorder, backgroundColor: theme.blockBg, borderRadius: 8, padding: 12, marginTop: 12 },
      blockTitle: { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 8 },
      twoCol: { flexDirection: 'row', gap: 12 },
      col: { flex: 1 },
      label: { fontSize: 11, color: '#6b7280' },
      value: { fontSize: 12, color: '#0f172a', fontWeight: '600', marginBottom: 4 },
      headerBand: { padding: 12, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent },
      headerBandText: { fontSize: 12, color: '#374151' },
      tableHeader: { flexDirection: 'row', backgroundColor: theme.tableHeaderBg, borderTopLeftRadius: 8, borderTopRightRadius: 8, borderBottomWidth: (isEmerald || isMinimal) ? 0 : 1, borderBottomColor: '#e5e7eb', borderTopWidth: isMinimal ? 1 : 0, borderTopColor: isMinimal ? '#e5e7eb' : undefined },
      th: { paddingVertical: 8, paddingHorizontal: 6, fontSize: 11, fontWeight: '700', color: (isEmerald || isSlate) ? '#ffffff' : theme.tableHeaderText, textTransform: isMinimal ? 'uppercase' : 'none', letterSpacing: isMinimal ? 1 : 0 },
      row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', alignItems: 'flex-start' },
      tdBox: { paddingVertical: 8, paddingHorizontal: 6 },
      td: { fontSize: 12, color: '#111827' },
      totals: { marginTop: 12, gap: 6 },
      totalsRow: { flexDirection: 'row', justifyContent: 'space-between' },
      totalsLabel: { fontSize: 12, color: '#374151' },
      totalsValue: { fontSize: 12, fontWeight: '700', color: '#0f172a' },
      grandRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
      grandLabel: { fontSize: 14, fontWeight: '800', color: '#111827' },
      grandValue: { fontSize: 16, fontWeight: '800', color: theme.grandColor },
      small: { fontSize: 11, color: '#6b7280' },
      signatureRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 24 },
      sigBlock: { width: 220, alignItems: 'center' },
      sigLine: { height: 1, backgroundColor: '#e5e7eb', alignSelf: 'stretch', marginTop: 24 },
      footerText: { position: 'absolute', bottom: 8, right: 12, fontSize: 9, color: '#9CA3AF' },
    };

    const hasHSN = !!columns.showHSN;
    const hasGST = !!columns.showGST;
    const hasImage = !!columns.showImage;

    return (
      <View style={printStyles.page}>
        <View style={printStyles.headerRow}>
          {isEmerald ? (
            <>
              <View style={{ flex: 1 }}>
                <Text style={[printStyles.invTitle, { color: theme.accent }]}>Quotation</Text>
                <Text style={printStyles.small}>Quotation No: {quote.number || '-'}</Text>
                <Text style={printStyles.small}>Quotation Date: {quote.quoteDate || '-'}</Text>
                {!!quote.validUntil && (<Text style={printStyles.small}>Valid Until: {quote.validUntil}</Text>)}
                {showDueDate && !!quote.dueDate && (<Text style={printStyles.small}>Due: {quote.dueDate}</Text>)}
              </View>
              <View style={[printStyles.brand, { alignSelf: 'flex-start' }]}>
                {logoUri ? (
                  <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
                ) : (
                  <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent }} />
                )}
                <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
              </View>
            </>
          ) : isSlate ? (
            <View style={{ backgroundColor: theme.accent, borderRadius: 10, padding: 14, marginBottom: 2, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <View style={[printStyles.brand, { gap: 10 }]}> 
                {logoUri ? (
                  <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
                ) : (
                  <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: '#FFFFFF22' }} />
                )}
                <Text style={[printStyles.brandText, { color: '#ffffff' }]}>{seller.businessName || 'Your Business'}</Text>
              </View>
              <View style={{ alignItems: 'flex-end' }}>
                <Text style={[printStyles.invTitle, { color: '#ffffff' }]}>{quote.title || 'Quotation'}</Text>
                <Text style={{ fontSize: 12, color: '#CBD5E1', marginTop: 4 }}>No: {quote.number || '-'}</Text>
                <Text style={{ fontSize: 12, color: '#CBD5E1' }}>Date: {quote.quoteDate || '-'}</Text>
              </View>
            </View>
          ) : isMinimal ? (
            <>
              <View style={{ flex: 1 }}>
                <Text style={[printStyles.invTitle]}>{quote.title || 'Quotation'}</Text>
                <View style={{ height: 2, backgroundColor: theme.accent, borderRadius: 1, marginTop: 6, marginBottom: 2 }} />
                <Text style={printStyles.small}>No: {quote.number || '-'}</Text>
                <Text style={printStyles.small}>Date: {quote.quoteDate || '-'}</Text>
              </View>
              <View style={printStyles.brand}>
                {logoUri ? (
                  <Image source={{ uri: logoUri }} style={{ width: 40, height: 40, borderRadius: 8 }} />
                ) : (
                  <View style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ffffff', borderWidth: 1, borderColor: '#e5e7eb' }} />
                )}
                <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
              </View>
            </>
          ) : (
            <>
              <View style={printStyles.brand}>
                {logoUri ? (
                  <Image source={{ uri: logoUri }} style={{ width: 48, height: 48, borderRadius: 8 }} />
                ) : (
                  <View style={{ width: 48, height: 48, borderRadius: 8, backgroundColor: theme.accentSoft, borderWidth: 1, borderColor: theme.accent }} />
                )}
                <Text style={printStyles.brandText}>{seller.businessName || 'Your Business'}</Text>
              </View>
              <View style={printStyles.invMetaRight}>
                <Text style={printStyles.invTitle}>{quote.title || 'Quotation'}</Text>
                <Text style={printStyles.invNo}>No: {quote.number || '-'}</Text>
                <Text style={printStyles.small}>Date: {quote.quoteDate || '-'}</Text>
                {!!quote.validUntil && (<Text style={printStyles.small}>Valid Until: {quote.validUntil}</Text>)}
                {showDueDate && !!quote.dueDate && (<Text style={printStyles.small}>Due: {quote.dueDate}</Text>)}
              </View>
            </>
          )}
        </View>

        <View style={[printStyles.block, printStyles.twoCol, isEmerald && { backgroundColor: theme.accentSoft, borderColor: theme.accent }]}>
          <View style={printStyles.col}>
            <Text style={printStyles.blockTitle}>Quoted By</Text>
            <Text style={printStyles.value}>{seller.businessName || '-'}</Text>
            {!!seller.address && (<Text style={printStyles.small}>{seller.address}</Text>)}
            <Text style={printStyles.small}>{seller.city || ''} {seller.postalCode || ''}</Text>
            {!!seller.country && (<Text style={printStyles.small}>{seller.country}</Text>)}
            {!!seller.gstin && (<Text style={printStyles.small}>GSTIN: {seller.gstin}</Text>)}
            {!!seller.pan && (<Text style={printStyles.small}>PAN: {seller.pan}</Text>)}
            {!!seller.email && (<Text style={printStyles.small}>Email: {seller.email}</Text>)}
            {!!seller.phone && (<Text style={printStyles.small}>Phone: {seller.phone}</Text>)}
            {(seller.customFields || []).map((f, idx) => (
              <Text key={idx} style={printStyles.small}>{(f.label || '—') + ': ' + (f.value || '—')}</Text>
            ))}
            {!!seller.state && (<Text style={[printStyles.small, { marginTop: 6 }]}>Place of Supply: {seller.state}</Text>)}
            {!!seller.country && (<Text style={printStyles.small}>Country of Supply: {seller.country}</Text>)}
          </View>
          <View style={printStyles.col}>
            <Text style={printStyles.blockTitle}>Quoted To</Text>
            <Text style={printStyles.value}>{client.businessName || '-'}</Text>
            {!!client.address && (<Text style={printStyles.small}>{client.address}</Text>)}
            <Text style={printStyles.small}>{client.city || ''} {client.postalCode || ''}</Text>
            {!!client.country && (<Text style={printStyles.small}>{client.country}</Text>)}
            {!!client.gstin && (<Text style={printStyles.small}>GSTIN: {client.gstin}</Text>)}
            {!!client.pan && (<Text style={printStyles.small}>PAN: {client.pan}</Text>)}
            {!!client.email && (<Text style={printStyles.small}>Email: {client.email}</Text>)}
            {!!client.phone && (<Text style={printStyles.small}>Phone: {client.phone}</Text>)}
            {(client.customFields || []).map((f, idx) => (
              <Text key={idx} style={printStyles.small}>{(f.label || '—') + ': ' + (f.value || '—')}</Text>
            ))}
            {!!client.state && (<Text style={[printStyles.small, { marginTop: 6 }]}>Place of Supply: {client.state}</Text>)}
            {!!client.country && (<Text style={printStyles.small}>Country of Supply: {client.country}</Text>)}
          </View>
        </View>

        {shipping.enabled && (
          <View style={[printStyles.block]}>
            <Text style={printStyles.blockTitle}>Shipping Details</Text>
            <Text style={printStyles.small}>{shipping.address}</Text>
            <Text style={printStyles.small}>{shipping.city} {shipping.postalCode}</Text>
          </View>
        )}

        {/* Items table */}
        <View style={printStyles.block}>
          <View style={printStyles.tableHeader}>
            {hasImage && (<Text style={[printStyles.th, { flex: 0.8 }]}>Image</Text>)}
            <Text style={[printStyles.th, { flex: 2 }]}>Item</Text>
            {hasHSN && (<Text style={[printStyles.th, { flex: 1 }]}>HSN/SAC</Text>)}
            {hasGST && (<Text style={[printStyles.th, { flex: 1 }]}>GST %</Text>)}
            <Text style={[printStyles.th, { flex: 1 }]}>Qty</Text>
            <Text style={[printStyles.th, { flex: 1 }]}>Rate</Text>
            <Text style={[printStyles.th, { flex: 1 }]}>Total</Text>
          </View>
          {items.map((line, idx) => {
            const baseAmount = (Number(line.qty) || 0) * (Number(line.rate) || 0);
            const taxAmt = baseAmount * ((Number(line.gstRate) || 0) / 100);
            const lineTotal = baseAmount + taxAmt;
            const rowStyle = [
              printStyles.row,
              isSlate && (idx % 2 === 1) ? { backgroundColor: '#F8FAFC' } : null,
              isMinimal ? { borderBottomColor: '#f3f4f6' } : null,
            ];
            return (
              <View key={line.id} style={rowStyle}>
                {hasImage && (
                  <View style={[printStyles.tdBox, { flex: 0.8 }]}> 
                    {!!line.imageUri ? (<Image source={{ uri: line.imageUri }} style={{ width: 32, height: 32, borderRadius: 4 }} />) : (<Text style={printStyles.small}>—</Text>)}
                  </View>
                )}
                <View style={[printStyles.tdBox, { flex: 2 }]}> 
                  <Text style={printStyles.td}>{line.name || '—'}</Text>
                  {!!line.description && (<Text style={printStyles.small}>{line.description}</Text>)}
                </View>
                {hasHSN && (
                  <View style={[printStyles.tdBox, { flex: 1 }]}> 
                    <Text style={printStyles.td}>{line.hsnSac || '—'}</Text>
                  </View>
                )}
                {hasGST && (
                  <View style={[printStyles.tdBox, { flex: 1 }]}> 
                    <Text style={printStyles.td}>{String(line.gstRate || 0)}%</Text>
                  </View>
                )}
                <View style={[printStyles.tdBox, { flex: 1 }]}> 
                  <Text style={printStyles.td}>{String(line.qty || 0)}</Text>
                </View>
                <View style={[printStyles.tdBox, { flex: 1 }]}> 
                  <Text style={printStyles.td}>{formatCurrency(Number(line.rate) || 0)}</Text>
                </View>
                <View style={[printStyles.tdBox, { flex: 1 }]}> 
                  <Text style={printStyles.td}>{formatCurrency(lineTotal)}</Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Totals area */}
        <View style={printStyles.block}>
          <View style={printStyles.totals}>
            <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Subtotal</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.subtotal)}</Text></View>
            <View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Tax</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.taxTotal)}</Text></View>
            {!!charges.addlCharges && (<View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Additional Charges</Text><Text style={printStyles.totalsValue}>{formatCurrency(summary.addl)}</Text></View>)}
            {!!charges.discount && (<View style={printStyles.totalsRow}><Text style={printStyles.totalsLabel}>Discount</Text><Text style={printStyles.totalsValue}>- {formatCurrency(summary.discount)}</Text></View>)}
          </View>
          <View style={printStyles.grandRow}><Text style={printStyles.grandLabel}>Grand Total</Text><Text style={[printStyles.grandValue]}>{formatCurrency(summary.total)}</Text></View>
          {charges.showTotalInWords && (
            <Text style={[printStyles.small, { marginTop: 6 }]}>Amount in words: {numberToWords(Math.round(summary.total))} {quote.currency}</Text>
          )}
        </View>

        {/* Notes, Terms, Contact, Additional Info */}
        {(terms || notes || contactDetails || additionalInfo) && (
          <View style={printStyles.block}>
            {!!terms && (<><Text style={printStyles.blockTitle}>Terms & Conditions</Text><Text style={printStyles.small}>{terms}</Text></>)}
            {!!notes && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Notes</Text><Text style={printStyles.small}>{notes}</Text></>)}
            {!!additionalInfo && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Additional Info</Text><Text style={printStyles.small}>{additionalInfo}</Text></>)}
            {!!contactDetails && (<><Text style={[printStyles.blockTitle, { marginTop: 8 }]}>Contact</Text><Text style={printStyles.small}>{contactDetails}</Text></>)}
          </View>
        )}

        {showSignature && !!signatureUri && (
          <View style={printStyles.signatureRow}>
            <View style={printStyles.sigBlock}>
              <Text style={printStyles.small}>Authorized Signatory</Text>
              <Image source={{ uri: signatureUri }} style={{ width: 200, height: 80, resizeMode: 'contain', marginTop: 8 }} />
              <View style={printStyles.sigLine} />
            </View>
          </View>
        )}

        {showAttachments && (attachments || []).length > 0 && (
          <View style={printStyles.block}>
            <Text style={printStyles.blockTitle}>Attachments</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {(attachments || []).filter(a => String(a||'').startsWith('data:image')).map((a, idx) => (
                <Image key={idx} source={{ uri: a }} style={{ width: 160, height: 100, resizeMode: 'contain', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 6 }} />
              ))}
            </View>
          </View>
        )}

        <Text style={printStyles.footerText}>Quotation generated by modernmunshiji.com</Text>
      </View>
    );
  };

  const memoPreview = useMemo(() => renderPrintQuotation(), [
    template,
    quote,
    seller,
    client,
    shipping,
    items,
    charges,
    notes,
    terms,
    contactDetails,
    additionalInfo,
    summary,
    logoUri,
    signatureUri,
    attachments,
  ]);

  const renderPreview = () => (
    <SectionCard title="Live Preview" right={(
      <View style={{ flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
        <Text style={[styles.helperText, { marginBottom: 2 }]}>Select Template</Text>
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
          <TouchableOpacity style={[styles.outlineButton, template==='classic' && styles.outlineButtonActive]} onPress={()=>setTemplate('classic')}><Text style={styles.outlineButtonText}>Template 1</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, template==='emerald' && styles.outlineButtonActive]} onPress={()=>setTemplate('emerald')}><Text style={styles.outlineButtonText}>Template 2</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, template==='minimal' && styles.outlineButtonActive]} onPress={()=>setTemplate('minimal')}><Text style={styles.outlineButtonText}>Template 3</Text></TouchableOpacity>
          <TouchableOpacity style={[styles.outlineButton, template==='slate' && styles.outlineButtonActive]} onPress={()=>setTemplate('slate')}><Text style={styles.outlineButtonText}>Template 4</Text></TouchableOpacity>
        </View>
      </View>
    )}>
      <View style={{ backgroundColor: 'transparent', alignItems: 'flex-start', overflow: 'hidden', width: '100%' }}>
        {/* Scale dimensions so the element width matches the available card width and anchor top-left on web */}
        <View style={{ width: 794 * previewScale, minHeight: 1123 * previewScale }}>
          <View style={[{ transform: [{ scale: previewScale }] }, Platform.OS === 'web' ? { transformOrigin: 'top left' } : null ]}>
            {memoPreview}
          </View>
        </View>
      </View>
    </SectionCard>
  );

  // Top hero section similar to Invoice Generator
  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroHeading}>Free Quotation Maker – Online Quote Generator</Text>
        <Text style={styles.heroDescription}>
          Create professional quotations with taxes, custom fields, and live preview. Export your quote as PDF instantly.
        </Text>
      </View>
      {isWeb && (
        <TouchableOpacity style={styles.primaryButton} onPress={exportPDF}>
          <Text style={styles.primaryButtonText}>Download PDF</Text>
        </TouchableOpacity>
      )}
    </View>
  );


  const getMonthKey = (isoDate) => {
    if (!isoDate) return null;
    try {
      const d = String(isoDate).split('T')[0];
      const [y, m] = d.split('-');
      if (y && m) return `${y}-${m}`;
      return null;
    } catch (_) { return null; }
  };

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saveInfo, setSaveInfo] = useState('');

  const saveQuotation = async () => {
    if (!session?.user?.id) {
      setSaveError('Please sign in to save your quotation.');
      return;
    }
    // Guard against invalid inputs or missing required data
    const hasQuoteErrors = Object.keys(errors.quote || {}).length > 0;
    const hasSellerErrors = Object.keys(errors.seller || {}).length > 0;
    const hasClientErrors = Object.keys(errors.client || {}).length > 0;
    const hasShippingErrors = shipping.enabled && Object.keys(errors.shipping || {}).length > 0;
    const hasItemErrors = items.some((it) => Object.keys((errors.items || {})[it.id] || {}).length > 0);
    const missingRequired = [];
    if (!validateRequired(quote.quoteDate)) missingRequired.push('Quote Date');
    if (showDueDate && !validateRequired(quote.dueDate)) missingRequired.push('Due Date');
    if (!validateRequired(seller.businessName)) missingRequired.push('Quoted By: Business Name');
    if (!validateRequired(client.businessName)) missingRequired.push('Quoted To: Business Name');
    if (!Array.isArray(items) || items.length === 0) missingRequired.push('At least one Item');

    const hasAnyIssues = hasQuoteErrors || hasSellerErrors || hasClientErrors || hasShippingErrors || hasItemErrors || missingRequired.length > 0;
    if (hasAnyIssues) {
      const parts = [];
      if (missingRequired.length) parts.push(`Required: ${missingRequired.join(', ')}`);
      if (hasQuoteErrors || hasSellerErrors || hasClientErrors || hasShippingErrors || hasItemErrors) parts.push('Fix highlighted field errors');
      const msg = `Cannot save yet. ${parts.join(' • ')}.`;
      setSaveMessage(msg);
      setSaveError('');
      try { Alert.alert('Incomplete Quotation', 'Please fill the required fields highlighted in red before saving.'); } catch (_) {}
      return;
    }
    setSaveMessage('');
    setSaving(true);
    setSaveError('');
    setSaveInfo('');
    const payload = {
      user_id: session.user.id,
      title: quote.title,
      quote_number: quote.number,
      quote_date: quote.quoteDate || null,
      valid_until: quote.validUntil || null,
      due_date: quote.dueDate || null,
      currency: quote.currency,
      seller,
      client,
      shipping: shipping.enabled ? shipping : null,
      items,
      charges,
      notes,
      terms,
      contact_details: contactDetails,
      additional_info: additionalInfo,
      attachments,
      logo_uri: logoUri || null,
      subtotal: summary.subtotal,
      tax_total: summary.taxTotal,
      total: summary.total,
      cgst_total: summary.cgstTotal,
      sgst_total: summary.sgstTotal,
      discount_total: summary.discount,
      additional_charges: summary.addl,
      total_in_words: numberToWords(summary.total),
      rounding_mode: charges.roundingMode,
      hide_totals: !!charges.hideTotals,
      summarize_qty: !!charges.summarizeQty,
      columns_config: columns,
      signature_uri: signatureUri,
      template_id: template,
      status: 'draft',
      billing_month: getMonthKey(quote.quoteDate) || getMonthKey(new Date().toISOString()),
    };

    // Try SDK insert with timeout fallback
    const selectPromise = supabase.from('quotation_maker').insert(payload).select('id').single();
    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve({ data: null, error: { message: 'Timed out contacting data service after 8s.', code: 'ETIMEOUT' } }), 8000));
    let { data, error } = await Promise.race([selectPromise, timeoutPromise]);

    if (error) {
      if (error.code === 'ETIMEOUT') {
        try {
          const token = session?.access_token || session?.accessToken || null;
          const url = `${SUPABASE_URL}/rest/v1/quotation_maker?select=id`;
          const headers = {
            apikey: SUPABASE_ANON_KEY,
            Accept: 'application/json',
            Authorization: token ? `Bearer ${token}` : `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            Prefer: 'return=representation',
          };
          const resp = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) });
          const text = await resp.text();
          let json = null;
          try { json = JSON.parse(text); } catch (_) {}
          if (resp.ok && json && (json.id || Array.isArray(json) && json[0]?.id)) {
            setSaveInfo('Quotation saved.');
            setSaveMessage('');
            setSaving(false);
            // Navigate to Dashboard → quotations after a brief success flash
            try { setTimeout(() => navigation.navigate('Dashboard', { section: 'quotations', sectionOnly: true }), 600); } catch (_) {}
            return;
          }
          setSaveError(`Save failed (${resp.status}). Please retry.`);
        } catch (e) {
          setSaveError(e?.message || 'Network save failed.');
        }
      } else {
        setSaveError(error.message);
      }
    } else {
      setSaveInfo('Quotation saved.');
      setSaveMessage('');
      setSaving(false);
      try { setTimeout(() => navigation.navigate('Dashboard', { section: 'quotations', sectionOnly: true }), 600); } catch (_) {}
      return;
    }
    setSaving(false);
  };

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      <View style={[styles.calculatorContainer]}>
        <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}> 
          <View style={[styles.formContent, !isMobile && { flex: leftPanelShare }, isMobile && { width: '100%' }]}> 
            {renderTopMeta()}
            {renderParties()}
            {renderItems()}
            {renderTotals()}
            <SectionCard title="Additional Details" right={(
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={[styles.outlineButton, showTerms && styles.outlineButtonActive]} onPress={()=>setShowTerms(p=>!p)}><Text style={styles.outlineButtonText}>{showTerms? 'Hide Terms' : 'Add Terms'}</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.outlineButton, showNotes && styles.outlineButtonActive]} onPress={()=>setShowNotes(p=>!p)}><Text style={styles.outlineButtonText}>{showNotes? 'Hide Notes' : 'Add Notes'}</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.outlineButton, showContact && styles.outlineButtonActive]} onPress={()=>setShowContact(p=>!p)}><Text style={styles.outlineButtonText}>{showContact? 'Hide Contact' : 'Add Contact'}</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.outlineButton, showSignature && styles.outlineButtonActive]} onPress={()=>setShowSignature(p=>!p)}><Text style={styles.outlineButtonText}>{showSignature? 'Hide Signature' : 'Add Signature'}</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.outlineButton, showAttachments && styles.outlineButtonActive]} onPress={()=>setShowAttachments(p=>!p)}><Text style={styles.outlineButtonText}>{showAttachments? 'Hide Attachments' : 'Add Attachments'}</Text></TouchableOpacity>
                <TouchableOpacity style={[styles.outlineButton, showAdditionalInfo && styles.outlineButtonActive]} onPress={()=>setShowAdditionalInfo(p=>!p)}><Text style={styles.outlineButtonText}>{showAdditionalInfo? 'Hide Info' : 'Add Info'}</Text></TouchableOpacity>
              </View>
            )}>
              <View style={{ gap: 8 }}>
                {showTerms && (
                  <View>
                    <Text style={styles.label}>Terms & Conditions</Text>
                    <TextInput style={[styles.input, { height: 80 }]} value={terms} onChangeText={setTerms} placeholder="Add Terms & Conditions" multiline />
                  </View>
                )}
                {showNotes && (
                  <View>
                    <Text style={styles.label}>Notes</Text>
                    <TextInput style={[styles.input, { height: 80 }]} value={notes} onChangeText={setNotes} placeholder="Add Notes" multiline />
                  </View>
                )}
                {showContact && (
                  <View>
                    <Text style={styles.label}>Contact Details</Text>
                    <TextInput style={[styles.input, { height: 40 }]} value={contactDetails} onChangeText={setContactDetails} placeholder="Name, Email, Phone" />
                  </View>
                )}
                {showSignature && (
                  <View>
                    <Text style={styles.label}>Signature</Text>
                    {isWeb ? (
                      <View>
                        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                          <TouchableOpacity style={styles.outlineButton} onPress={()=>{ try { signatureInputRef.current?.click(); } catch(_){} }}>
                            <Text style={styles.outlineButtonText}>Upload Signature</Text>
                          </TouchableOpacity>
                          <input ref={signatureInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleSignaturePick} />
                        </View>
                        {!!signatureUri && (
                          <View style={{ marginTop: 8 }}>
                            <Image source={{ uri: signatureUri }} style={{ width: 160, height: 80, resizeMode: 'contain' }} />
                          </View>
                        )}
                      </View>
                    ) : (
                      <TextInput style={[styles.input, { height: 40 }]} value={signatureUri} onChangeText={setSignatureUri} placeholder="https://.../signature.png" />
                    )}
                  </View>
                )}
                {showAttachments && (
                  <View>
                    <Text style={styles.label}>Attachments</Text>
                    {isWeb && (
                      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <TouchableOpacity style={styles.outlineButton} onPress={()=>{ try { attachmentsInputRef.current?.click(); } catch(_){} }}>
                          <Text style={styles.outlineButtonText}>Upload Image</Text>
                        </TouchableOpacity>
                        <input ref={attachmentsInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAttachmentPick} />
                      </View>
                    )}
                    {(attachments || []).map((a, idx) => (
                      <View key={idx} style={{ marginBottom: 8 }}>
                        {String(a||'').startsWith('data:image') && (
                          <View>
                            <Image source={{ uri: a }} style={{ width: 160, height: 100, resizeMode: 'contain', borderWidth: 1, borderColor: '#ddd', borderRadius: 6 }} />
                            <View style={{ marginTop: 4 }}>
                              <TouchableOpacity style={styles.iconButton} onPress={()=>setAttachments(prev=>prev.filter((_,i)=>i!==idx))}><Text>✕</Text></TouchableOpacity>
                            </View>
                          </View>
                        )}
                      </View>
                    ))}
                  </View>
                )}
                {showAdditionalInfo && (
                  <View>
                    <Text style={styles.label}>Additional Info</Text>
                    <TextInput style={[styles.input, { height: 80 }]} value={additionalInfo} onChangeText={setAdditionalInfo} placeholder="Any extra details" multiline />
                  </View>
                )}
              </View>
            </SectionCard>
            <SectionCard title="Shipping Details" right={(
              <TouchableOpacity style={styles.outlineButton} onPress={()=>setShipping(p=>({ ...p, enabled: !p.enabled }))}><Text style={styles.outlineButtonText}>{shipping.enabled? 'Hide' : 'Add Shipping Details'}</Text></TouchableOpacity>
            )}>
              {shipping.enabled && (
                <View style={{ gap: 8 }}>
                  <View style={styles.inputRow}>
                    <View style={styles.half}>
                      <Text style={styles.label}>Address</Text>
                      <TextInput style={styles.input} value={shipping.address} onChangeText={(v)=>setShipping(p=>({ ...p, address: v }))} placeholder="Shipping address" />
                    </View>
                    <View style={styles.half}>
                      <Text style={styles.label}>City</Text>
                      <TextInput style={styles.input} value={shipping.city} onChangeText={(v)=>setShipping(p=>({ ...p, city: v }))} placeholder="City" />
                    </View>
                  </View>
                  <View style={styles.inputRow}>
                    <View style={styles.half}>
                      <Text style={styles.label}>Postal Code</Text>
                      <TextInput style={[styles.input, errors.shipping?.postalCode && styles.inputError]} value={shipping.postalCode} onChangeText={(v)=>setShipping(p=>({ ...p, postalCode: v }))} placeholder="123456" />
                      {!!errors.shipping?.postalCode && (<Text style={styles.errorText}>{errors.shipping.postalCode}</Text>)}
                    </View>
                    <View style={styles.half}>
                      <Text style={styles.label}>State</Text>
                      <TextInput style={styles.input} value={shipping.state} onChangeText={(v)=>setShipping(p=>({ ...p, state: v }))} placeholder="State" />
                    </View>
                  </View>
                  <Text style={styles.label}>Notes</Text>
                  <TextInput style={[styles.input, { height: 60 }]} value={shipping.notes} onChangeText={(v)=>setShipping(p=>({ ...p, notes: v }))} placeholder="Shipping notes" multiline />
                </View>
              )}
            </SectionCard>
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginTop: 12, marginBottom: 8 }}>
              <TouchableOpacity style={[styles.outlineButton, saving && styles.outlineButtonActive]} onPress={saveQuotation} disabled={saving}>
                {saving ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <ActivityIndicator size="small" color="#4F46E5" />
                    <Text style={styles.outlineButtonText}>Saving…</Text>
                  </View>
                ) : (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Ionicons name="save-outline" size={16} color="#4F46E5" />
                    <Text style={styles.outlineButtonText}>Save & Continue</Text>
                  </View>
                )}
              </TouchableOpacity>
              {isWeb && (
                <TouchableOpacity style={styles.outlineButton} onPress={exportPDF}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Ionicons name="download-outline" size={16} color="#4F46E5" />
                    <Text style={styles.outlineButtonText}>Download PDF</Text>
                  </View>
                </TouchableOpacity>
              )}
              {!!saveInfo && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Ionicons name="checkmark-circle" size={18} color="#065f46" />
                  <Text style={[styles.helperText, { color: '#065f46' }]}>Quotation saved.</Text>
                </View>
              )}
              {!!saveMessage && !saving && (
                <Text style={styles.helperText}>{saveMessage}</Text>
              )}
              {!!saveError && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Ionicons name="alert-circle-outline" size={18} color="#b91c1c" />
                  <Text style={[styles.helperText, { color: '#b91c1c' }]}>{saveError}</Text>
                </View>
              )}
            </View>
            <SectionCard title="Why Choose Modern Munshiji Quotation Maker">
              <View style={{ gap: 8 }}>
                <Text style={styles.helperText}>• Fast, clean interface tailored for Indian GST needs.</Text>
                <Text style={styles.helperText}>• Live preview matches your final downloadable PDF.</Text>
                <Text style={styles.helperText}>• Save & continue for reuse; nothing stored without your action.</Text>
              </View>
            </SectionCard>
            <SectionCard title="How to Use Modern Munshiji Quotation Maker">
              <View style={{ gap: 8 }}>
                <Text style={styles.helperText}>• Enter Quote No and dates in Quotation Details.</Text>
                <Text style={styles.helperText}>• Fill your and client details including GSTIN and PAN.</Text>
                <Text style={styles.helperText}>• Add line items, GST%, and see totals update live.</Text>
                <Text style={styles.helperText}>• Toggle discounts/charges and show total in words.</Text>
                <Text style={styles.helperText}>• Click Download PDF to save or share.</Text>
              </View>
            </SectionCard>
            <SectionCard title="Quotation Maker FAQs">
              <View style={{ gap: 12 }}>
                <View>
                  <Text style={styles.label}>Is it free?</Text>
                  <Text style={styles.helperText}>Yes, creating and downloading quotes is completely free to use.</Text>
                </View>
                <View>
                  <Text style={styles.label}>Can I add GST?</Text>
                  <Text style={styles.helperText}>Yes, set GST% per line item. Totals calculate automatically.</Text>
                </View>
                <View>
                  <Text style={styles.label}>Can I upload my logo/signature?</Text>
                  <Text style={styles.helperText}>Use the Upload options in Additional Details to brand your quote.</Text>
                </View>
                <View>
                  <Text style={styles.label}>Do you store my data?</Text>
                  <Text style={styles.helperText}>No, quotes are generated in your browser and not stored on our servers.</Text>
                </View>
              </View>
            </SectionCard>
          </View>
          <View style={[styles.summaryPanel, !isMobile && { flex: rightPanelShare }, isMobile && { width: '100%', marginTop: 16 }]}> 
            {renderPreview()}
          </View>
        </View>
      </View>
      {isWeb && (
        <View nativeID="quotation-print" style={{ position: 'absolute', left: -10000, top: 0 }} pointerEvents="none" aria-hidden="true" tabIndex={-1} importantForAccessibility="no-hide-descendants">
          {memoPreview}
        </View>
      )}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 40, paddingHorizontal: 30, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  heroContent: { maxWidth: 900 },
  heroHeading: { color: '#ffffff', fontSize: 30, fontWeight: '800' },
  heroDescription: { color: '#E5E7EB', fontSize: 14, marginTop: 6 },
  primaryButton: { backgroundColor: '#ffffff', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, borderWidth: 1, borderColor: '#ffffff' },
  primaryButtonText: { color: '#4F46E5', fontWeight: '700', fontSize: 12 },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 16 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { flex: 0.7, gap: 12 },
  summaryPanel: { flex: 0.3, alignItems: 'flex-start' },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, borderWidth: 1, borderColor: '#e5e7eb', marginBottom: 16 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  cardTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  input: { backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 40, fontSize: 14, color: '#0f172a' },
  inputError: { borderColor: '#ef4444', backgroundColor: '#fff1f2' },
  outlineButton: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: '#4F46E5' },
  outlineButtonActive: { backgroundColor: '#4F46E515' },
  outlineButtonText: { color: '#4F46E5', fontWeight: '600', fontSize: 12 },
  itemHeaderRow: { flexDirection: 'row', backgroundColor: '#4F46E5', borderTopLeftRadius: 8, borderTopRightRadius: 8, paddingVertical: 10, paddingHorizontal: 8, marginTop: 8 },
  itemHeaderCell: { color: '#ffffff', fontWeight: '600', fontSize: 12, textAlign: 'left' },
  itemRow: { flexDirection: 'row', backgroundColor: '#ffffff', borderBottomWidth: 1, borderColor: '#e5e7eb', paddingVertical: 8, paddingHorizontal: 8, alignItems: 'flex-start', gap: 6 },
  itemCell: { paddingHorizontal: 6, justifyContent: 'center' },
  itemValue: { fontSize: 14, color: '#0f172a' },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 14, fontWeight: '600', color: '#0f172a' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  helperText: { fontSize: 12, color: '#64748b' },
  errorText: { fontSize: 12, color: '#b91c1c', marginTop: 4 },
  iconButton: { padding: 6, borderRadius: 6, borderWidth: 1, borderColor: '#e5e7eb' },
});

export default QuotationMakerScreen;